import { Component, computed, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router, RouterModule } from '@angular/router';

import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@layout/service/layout.service';
import { Ripple } from 'primeng/ripple';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { FormsModule } from '@angular/forms';
import { AppSidebarComponent } from '@layout/components/app.sidebar';
import { AppBreadcrumbComponent } from '@layout/components/app.breadcrumb';
import { AuthService } from '@auth/services/auth.service';
import { AvatarModule } from 'primeng/avatar';
import { DividerModule } from 'primeng/divider';
import { AppModuleSelectorComponent } from './app.module-selector';
import { CommandPaletteService } from '@core/command-palette/command-palette.service';
import { NotificationBellComponent } from '@core/notifications/components/notification-bell.component';
import { UpdateService } from '@core/services/update.service';
import { KerepLogoComponent } from '@shared/components/kerep-logo/kerep-logo.component';
import { KEREP_PRODUCT } from '@shared/constants/brand.constants';
import { BranchSwitcherComponent } from '@auth/components/branch-switcher.component';
import { AppAboutDialogComponent } from '@layout/components/app.about-dialog';
import { environment } from '@/environments/environment';
import { isPackagedApp } from '@shared/utils/platform.utils';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-topbar]',
  standalone: true,
  imports: [
    RouterModule,
    StyleClassModule,
    FormsModule,
    AppModuleSelectorComponent,
    Ripple,
    ButtonModule,
    AppBreadcrumbComponent,
    AppSidebarComponent,
    ChipModule,
    AvatarModule,
    DividerModule,
    NotificationBellComponent,
    KerepLogoComponent,
    AppAboutDialogComponent,
    BranchSwitcherComponent,
  ],
  template: `
    @if (isImpersonating()) {
      <div
        class="fixed top-0 left-0 w-full z-[9999] bg-orange-500 text-white text-center py-2 px-4 flex items-center justify-center gap-3 text-sm font-medium shadow-md"
      >
        <i class="pi pi-exclamation-triangle"></i>
        <span>Sesión de soporte activa — Estás operando como otro usuario</span>
        <button
          pButton
          pRipple
          type="button"
          label="Terminar suplantación"
          aria-label="Terminar suplantación"
          icon="pi pi-sign-out"
          class="p-button-sm p-button-outlined"
          severity="contrast"
          [loading]="endingImpersonation()"
          (click)="onEndImpersonation()"
        ></button>
      </div>
    }
    <div class="topbar-start">
      <div class="topbar-main-row">
        <button
          pButton
          pRipple
          #menubutton
          type="button"
          class="topbar-menubutton p-trigger"
          text
          rounded
          severity="secondary"
          (click)="onMenuButtonClick()"
        >
          <i class="pi pi-bars"></i>
        </button>

        <!-- Selector de módulos horizontal - Solo visible en desktop -->
        <app-module-selector></app-module-selector>
      </div>

      <div class="topbar-breadcrumb">
        <div app-breadcrumb></div>
      </div>
    </div>
    @if (layoutService.isHorizontal()) {
      <div class="layout-topbar-menu-section">
        <div app-sidebar></div>
      </div>
    }
    <div class="topbar-end">
      <ul class="topbar-menu">
        <li data-tour="topbar.search" class="md:flex-1 md:min-w-[16rem] md:max-w-[28rem]">
          <!-- Disparador del buscador global: ícono en móvil, barra ancha en escritorio -->
          <button
            type="button"
            class="flex items-center gap-3 w-full px-3 py-2 rounded-lg border border-surface-300 dark:border-surface-600 text-surface-500 hover:border-primary hover:text-primary transition-colors cursor-pointer"
            title="Buscar (Ctrl/⌘ + K)"
            aria-label="Buscar (Ctrl/⌘ + K)"
            (click)="openCommandPalette()"
          >
            <i class="pi pi-search text-base"></i>
            <span class="hidden md:inline flex-1 text-left text-sm">Buscar páginas, módulos…</span>
            <span
              class="hidden md:inline text-xs font-medium border border-surface-300 dark:border-surface-600 rounded px-1.5 py-0.5"
              >⌘K</span
            >
          </button>
        </li>
        <li>
          <app-notification-bell></app-notification-bell>
        </li>
        <li>
          <button
            pButton
            pRipple
            type="button"
            icon="pi pi-question-circle"
            class="flex-shrink-0"
            text
            rounded
            title="Ayuda y documentación"
            [routerLink]="['/ayuda']"
          ></button>
        </li>

        <!-- Sede activa: va antes del tema y del perfil porque es CONTEXTO de la sesión, no una
             preferencia. El componente se oculta solo si el usuario tiene una sola sede. -->
        <li class="ml-2">
          <app-branch-switcher />
        </li>

        <li class="ml-4">
          <button
            pButton
            pRipple
            type="button"
            [icon]="darkTheme() ? 'pi pi-sun' : 'pi pi-moon'"
            [attr.aria-label]="darkTheme() ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'"
            class="flex-shrink-0 config-button"
            text
            rounded
            (click)="onConfigButtonClick()"
          ></button>
        </li>

        <li class="profile-item topbar-item">
          <a
            pStyleClass="@next"
            enterFromClass="!hidden"
            enterActiveClass="animate-scalein"
            leaveToClass="!hidden"
            leaveActiveClass="animate-fadeout"
            [hideOnOutsideClick]="true"
            class="cursor-pointer"
          >
            @if (avatarError()) {
              <p-avatar label="{{ username() }}" styleClass="mr-2" size="large" shape="circle" />
            } @else {
              <p-avatar
                [image]="pathImageProfile()"
                (onImageError)="avatarError.set(true)"
                [style]="{ 'background-color': '#dee9fc', color: '#1a2551' }"
                styleClass="mr-2"
                size="large"
                shape="circle"
              />
            }
          </a>

          <ul class="topbar-menu active-topbar-menu !p-6 w-60 z-50 !hidden rounded">
            <li role="menuitem" class="!m-0 !mb-4">
              <div class="bg-primary-100 dark:bg-primary-900 p-1 rounded-lg">
                <i class="pi pi-fw pi-user mr-2"></i>
                <span>Usuario</span>
                <br />
                <span class="font-bold text-primary-900 dark:text-primary-100 text-lg">{{ fullName() }}</span>
              </div>
            </li>

            <li role="menuitem" class="!m-0 !mb-2">
              <a
                class="flex items-center hover:text-primary-500 duration-200 cursor-pointer"
                pStyleClass="@grandparent"
                enterFromClass="!hidden"
                enterActiveClass="animate-scalein"
                leaveToClass="!hidden"
                leaveActiveClass="animate-fadeout"
                [hideOnOutsideClick]="true"
                tabindex="0"
                (click)="navigateToMyAccount()"
                (keydown.enter)="navigateToMyAccount()"
              >
                <i class="pi pi-fw pi-user-edit mr-2" aria-hidden="true"></i>
                <span>Mi Cuenta</span>
              </a>
            </li>
            <li class="!m-0 !mb-2">
              <p-divider styleClass="!my-1" />
            </li>
            @if (!isPackaged) {
              <li role="menuitem" class="!m-0 !mb-2">
                <a
                  class="flex items-center hover:text-primary-500 duration-200 cursor-pointer"
                  pStyleClass="@grandparent"
                  enterFromClass="!hidden"
                  enterActiveClass="animate-scalein"
                  leaveToClass="!hidden"
                  leaveActiveClass="animate-fadeout"
                  [hideOnOutsideClick]="true"
                  tabindex="0"
                  title="Recarga la app desde cero limpiando la caché. Útil si la ves desactualizada o con errores tras una actualización. No cierra tu sesión."
                  (click)="onCleanReload()"
                  (keydown.enter)="onCleanReload()"
                >
                  <i class="pi pi-fw pi-refresh mr-2" aria-hidden="true"></i>
                  <span>Recargar app (limpiar caché)</span>
                </a>
              </li>
            }
            <li role="menuitem" class="!m-0 ">
              <a
                href="#"
                class="flex items-center hover:text-primary-500 duration-200"
                pStyleClass="@grandparent"
                enterFromClass="!hidden"
                enterActiveClass="animate-scalein"
                leaveToClass="!hidden"
                leaveActiveClass="animate-fadeout"
                [hideOnOutsideClick]="true"
                (click)="authService.closeSession()"
              >
                <i class="pi pi-fw pi-sign-out mr-2"></i>
                <span>Cerrar sesión</span>
              </a>
            </li>

            <!-- La firma ES el acceso al "acerca de": es donde el usuario ya la buscaba.
                 Apilada porque el lockup y la versión no caben en línea dentro de los
                 15rem del menú. (El desbordamiento por la derecha que antes obligaba a
                 esto está corregido en _topbar.scss.) -->
            <li role="menuitem" class="!m-0 !mt-4 !pt-3 border-t border-surface-200 dark:border-surface-700">
              <a
                class="block cursor-pointer hover:opacity-80 transition-opacity"
                pStyleClass="@grandparent"
                enterFromClass="!hidden"
                enterActiveClass="animate-scalein"
                leaveToClass="!hidden"
                leaveActiveClass="animate-fadeout"
                [hideOnOutsideClick]="true"
                tabindex="0"
                title="Acerca de Kerep Gestión"
                (click)="openAbout()"
                (keydown.enter)="openAbout()"
              >
                <span class="font-kerep flex items-center text-[var(--kerep-brand)]">
                  <shared-kerep-logo variant="lockup" [product]="brandProduct" [size]="22" />
                </span>
                <span class="block text-xs text-muted-color mt-1">Versión {{ appVersion }}</span>
              </a>
            </li>
          </ul>
        </li>
      </ul>
    </div>

    <app-about-dialog [(visible)]="aboutVisible" />
  `,
  host: {
    class: 'layout-topbar',
  },
})
export class AppTopbarComponent {
  protected get brandProduct(): string {
    return KEREP_PRODUCT;
  }

  menu: MenuItem[] = [];

  @ViewChild('searchinput') searchInput!: ElementRef<HTMLElement>;

  @ViewChild('menubutton') menuButton!: ElementRef<HTMLElement>;

  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

  el = inject(ElementRef);
  authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly commandPalette = inject(CommandPaletteService);
  private readonly updateService = inject(UpdateService);

  openCommandPalette(): void {
    this.commandPalette.open();
  }

  isImpersonating = computed(() => this.authService.isImpersonating());
  endingImpersonation = signal(false);

  avatarError = signal(false);

  /** Se muestra junto a la firma de marca, al pie del menú de perfil. */
  readonly appVersion = environment.version;

  /** Diálogo "Acerca de", que abre la propia firma del menú de perfil. */
  readonly aboutVisible = signal(false);

  /**
   * En apps empaquetadas (Tauri/Capacitor) no hay Service Worker ni caché de
   * navegador que limpiar: la actualización va por el updater propio de cada
   * plataforma. El ítem "Recargar app (limpiar caché)" no aplica ahí.
   */
  protected readonly isPackaged = isPackagedApp();

  /**
   * El cierre del menú lo hace `pStyleClass`, igual que en el resto de items.
   *
   * NO lo cierres a mano tocando las clases del `<ul>`: `pStyleClass` mantiene su
   * propio estado de abierto/cerrado y manipularlo por fuera lo desincroniza, de
   * modo que el avatar deja de reabrirlo (comprobado el 2026-07-29).
   */
  openAbout(): void {
    this.aboutVisible.set(true);
  }

  username = computed(() => this.authService.user()?.username?.split('')[0].toUpperCase());

  pathImageProfile = computed(() => this.authService.user()?.pathImageProfile || undefined);

  fullName = computed(() => `${this.authService.user()?.firstName} ${this.authService.user()?.lastName}`);
  darkTheme = computed(() => this.layoutService.layoutConfig().darkTheme);
  public layoutService = inject(LayoutService);

  searchBarActive = computed(() => this.layoutService.layoutState().searchBarActive);

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  activateSearch() {
    this.layoutService.layoutState.update((val) => ({
      ...val,
      searchBarActive: true,
    }));
    setTimeout(() => {
      this.searchInput.nativeElement?.focus();
    }, 250);
  }

  deactivateSearch() {
    this.layoutService.layoutState.update((val) => ({
      ...val,
      searchBarActive: false,
    }));
  }

  onConfigButtonClick() {
    // this.layoutService.showConfigSidebar();
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      darkTheme: !state.darkTheme,
    }));
  }

  onSidebarButtonClick() {
    this.layoutService.layoutState.update((val) => ({
      ...val,
      rightMenuVisible: true,
    }));
  }

  onProfileMenuButtonClick() {
    this.layoutService.layoutState.update((val) => ({
      ...val,
      rightMenuActive: true,
    }));
  }

  navigateToMyAccount() {
    this.router.navigate(['/general-settings/my-account']);
  }

  /** Acción manual de recuperación: carga limpia (desregistra SW + borra cachés + recarga). No cierra sesión. */
  onCleanReload() {
    void this.updateService.forceCleanReload();
  }

  onEndImpersonation() {
    this.endingImpersonation.set(true);
    this.authService.endImpersonation().subscribe({
      complete: () => this.endingImpersonation.set(false),
      error: () => this.endingImpersonation.set(false),
    });
  }
}
